import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

const initialState = {
  clients: [],
  requirements: [],
  candidates: [],
  currentClient: null,
  currentRequirement: null,
  currentCandidate: null,
  requirementStats: null,
  candidateStats: null,
  pagination: null,
  loading: false,
  error: null
};

// Clients
export const fetchClients = createAsyncThunk(
  'staffing/fetchClients',
  async ({ page = 1, limit = 10, search } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ page, limit });
      if (search) params.append('search', search);
      
      const response = await api.get(`/staffing/clients?${params}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch clients');
    }
  }
);

export const fetchClientById = createAsyncThunk(
  'staffing/fetchClientById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/staffing/clients/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch client');
    }
  }
);

export const createClient = createAsyncThunk(
  'staffing/createClient',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/staffing/clients', data);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create client');
    }
  }
);

export const updateClient = createAsyncThunk(
  'staffing/updateClient',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/staffing/clients/${id}`, data);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update client');
    }
  }
);

export const deleteClient = createAsyncThunk(
  'staffing/deleteClient',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/staffing/clients/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete client');
    }
  }
);

// Requirements
export const fetchRequirements = createAsyncThunk(
  'staffing/fetchRequirements',
  async ({ page = 1, limit = 10, client_id, status, search } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ page, limit });
      if (client_id) params.append('client_id', client_id);
      if (status) params.append('status', status);
      if (search) params.append('search', search);
      
      const response = await api.get(`/staffing/requirements?${params}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch requirements');
    }
  }
);

export const fetchRequirementById = createAsyncThunk(
  'staffing/fetchRequirementById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/staffing/requirements/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch requirement');
    }
  }
);

export const createRequirement = createAsyncThunk(
  'staffing/createRequirement',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/staffing/requirements', data);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create requirement');
    }
  }
);

export const updateRequirement = createAsyncThunk(
  'staffing/updateRequirement',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/staffing/requirements/${id}`, data);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update requirement');
    }
  }
);

export const deleteRequirement = createAsyncThunk(
  'staffing/deleteRequirement',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/staffing/requirements/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete requirement');
    }
  }
);

// Candidates
export const fetchCandidates = createAsyncThunk(
  'staffing/fetchCandidates',
  async ({ page = 1, limit = 10, requirement_id, client_id, status, search } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ page, limit });
      if (requirement_id) params.append('requirement_id', requirement_id);
      if (client_id) params.append('client_id', client_id);
      if (status) params.append('status', status);
      if (search) params.append('search', search);
      
      const response = await api.get(`/staffing/candidates?${params}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch candidates');
    }
  }
);

export const fetchCandidateById = createAsyncThunk(
  'staffing/fetchCandidateById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/staffing/candidates/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch candidate');
    }
  }
);

export const createCandidate = createAsyncThunk(
  'staffing/createCandidate',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/staffing/candidates', data);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create candidate');
    }
  }
);

export const updateCandidate = createAsyncThunk(
  'staffing/updateCandidate',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/staffing/candidates/${id}`, data);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update candidate');
    }
  }
);

export const deleteCandidate = createAsyncThunk(
  'staffing/deleteCandidate',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/staffing/candidates/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete candidate');
    }
  }
);

// Stats
export const fetchRequirementStats = createAsyncThunk(
  'staffing/fetchRequirementStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/staffing/requirements/stats');
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch stats');
    }
  }
);

export const fetchCandidateStats = createAsyncThunk(
  'staffing/fetchCandidateStats',
  async (requirement_id, { rejectWithValue }) => {
    try {
      const params = requirement_id ? `?requirement_id=${requirement_id}` : '';
      const response = await api.get(`/staffing/candidates/stats${params}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch stats');
    }
  }
);

const staffingSlice = createSlice({
  name: 'staffing',
  initialState,
  reducers: {
    clearCurrentClient: (state) => {
      state.currentClient = null;
    },
    clearCurrentRequirement: (state) => {
      state.currentRequirement = null;
    },
    clearCurrentCandidate: (state) => {
      state.currentCandidate = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Clients
      .addCase(fetchClients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.loading = false;
        state.clients = action.payload.clients;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Client By ID
      .addCase(fetchClientById.fulfilled, (state, action) => {
        state.currentClient = action.payload;
      })
      // Create Client
      .addCase(createClient.fulfilled, (state, action) => {
        state.clients.unshift(action.payload);
      })
      // Update Client
      .addCase(updateClient.fulfilled, (state, action) => {
        const index = state.clients.findIndex(c => c._id === action.payload._id);
        if (index !== -1) {
          state.clients[index] = action.payload;
        }
        if (state.currentClient?._id === action.payload._id) {
          state.currentClient = action.payload;
        }
      })
      // Delete Client
      .addCase(deleteClient.fulfilled, (state, action) => {
        state.clients = state.clients.filter(c => c._id !== action.payload);
      })
      // Fetch Requirements
      .addCase(fetchRequirements.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRequirements.fulfilled, (state, action) => {
        state.loading = false;
        state.requirements = action.payload.requirements;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchRequirements.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Requirement By ID
      .addCase(fetchRequirementById.fulfilled, (state, action) => {
        state.currentRequirement = action.payload;
      })
      // Create Requirement
      .addCase(createRequirement.fulfilled, (state, action) => {
        state.requirements.unshift(action.payload);
      })
      // Update Requirement
      .addCase(updateRequirement.fulfilled, (state, action) => {
        const index = state.requirements.findIndex(r => r._id === action.payload._id);
        if (index !== -1) {
          state.requirements[index] = action.payload;
        }
        if (state.currentRequirement?._id === action.payload._id) {
          state.currentRequirement = action.payload;
        }
      })
      // Delete Requirement
      .addCase(deleteRequirement.fulfilled, (state, action) => {
        state.requirements = state.requirements.filter(r => r._id !== action.payload);
      })
      // Fetch Candidates
      .addCase(fetchCandidates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCandidates.fulfilled, (state, action) => {
        state.loading = false;
        state.candidates = action.payload.candidates;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchCandidates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Candidate By ID
      .addCase(fetchCandidateById.fulfilled, (state, action) => {
        state.currentCandidate = action.payload;
      })
      // Create Candidate
      .addCase(createCandidate.fulfilled, (state, action) => {
        state.candidates.unshift(action.payload);
      })
      // Update Candidate
      .addCase(updateCandidate.fulfilled, (state, action) => {
        const index = state.candidates.findIndex(c => c._id === action.payload._id);
        if (index !== -1) {
          state.candidates[index] = action.payload;
        }
        if (state.currentCandidate?._id === action.payload._id) {
          state.currentCandidate = action.payload;
        }
      })
      // Delete Candidate
      .addCase(deleteCandidate.fulfilled, (state, action) => {
        state.candidates = state.candidates.filter(c => c._id !== action.payload);
      })
      // Stats
      .addCase(fetchRequirementStats.fulfilled, (state, action) => {
        state.requirementStats = action.payload;
      })
      .addCase(fetchCandidateStats.fulfilled, (state, action) => {
        state.candidateStats = action.payload;
      });
  }
});

export const { clearCurrentClient, clearCurrentRequirement, clearCurrentCandidate, clearError } = staffingSlice.actions;
export default staffingSlice.reducer;
